import { Component } from '@theme/component';
import { fetchConfig, debounce, onAnimationEnd, prefersReducedMotion, resetShimmer } from '@theme/utilities';
import { morphSection, sectionRenderer } from '@theme/section-renderer';
import {
  ThemeEvents,
  CartUpdateEvent,
  QuantitySelectorUpdateEvent,
  CartAddEvent,
  DiscountUpdateEvent,
} from '@theme/events';
import { cartPerformance } from '@theme/performance';

/** @typedef {import('./utilities').TextComponent} TextComponent */

/**
 * A custom element that displays a cart items component.
 *
 * @typedef {object} Refs
 * @property {HTMLElement[]} quantitySelectors - The quantity selector elements.
 * @property {HTMLTableRowElement[]} cartItemRows - The cart item rows.
 * @property {TextComponent} cartTotal - The cart total.
 *
 * @extends {Component<Refs>}
 */
class CartItemsComponent extends Component {
  #debouncedOnChange = debounce(this.#onQuantityChange, 300).bind(this);

  /**
   * Serializes cart mutations. Cart endpoints address line items either by
   * stable key or by 1-based position; positions shift the moment any line
   * is added or removed, so overlapping in-flight requests race and corrupt
   * each other. Every mutation is chained onto this promise so only one is
   * ever in flight.
   * @type {Promise<unknown>}
   */
  #mutationChain = Promise.resolve();

  /** @type {number} Count of queued/in-flight mutations. */
  #pendingMutations = 0;

  /**
   * Enqueues a cart mutation so it runs after all prior mutations settle.
   * @param {() => Promise<unknown>} task
   * @returns {Promise<unknown>}
   */
  #enqueueMutation(task) {
    this.#pendingMutations++;
    this.#disableCartItems();
    const run = this.#mutationChain.then(task, task);
    // Swallow rejections on the chain itself so one failure can't poison
    // every subsequent mutation; the task handles its own errors.
    this.#mutationChain = run.then(
      () => this.#onMutationSettled(),
      () => this.#onMutationSettled()
    );
    return run;
  }

  /** Re-enables cart interaction once the mutation queue has fully drained. */
  #onMutationSettled() {
    this.#pendingMutations = Math.max(0, this.#pendingMutations - 1);
    if (this.#pendingMutations === 0) {
      this.#enableCartItems();
      resetShimmer(this);
    }
  }

  connectedCallback() {
    super.connectedCallback();

    document.addEventListener(ThemeEvents.cartUpdate, this.#handleCartUpdate);
    document.addEventListener(ThemeEvents.discountUpdate, this.handleDiscountUpdate);
    document.addEventListener(ThemeEvents.quantitySelectorUpdate, this.#debouncedOnChange);

    // Set up gallery wall bundle toggle listeners
    this.#setupGalleryWallToggles();
  }

  disconnectedCallback() {
    super.disconnectedCallback();

    document.removeEventListener(ThemeEvents.cartUpdate, this.#handleCartUpdate);
    document.removeEventListener(ThemeEvents.quantitySelectorUpdate, this.#debouncedOnChange);
  }

  /**
   * Handles QuantitySelectorUpdateEvent change event.
   * @param {QuantitySelectorUpdateEvent} event - The event.
   */
  #onQuantityChange(event) {
    const { quantity, cartLine: line, cartKey: key } = event.detail;

    // First, check if the event came from within a gallery wall bundle
    // Gallery wall bundles use galleryWallId, not line number, so check this BEFORE validating line
    // Use event.target to find the containing bundle row (more reliable than index-based lookup
    // since bundles collapse multiple cart items into one DOM row)
    const targetElement = /** @type {HTMLElement} */ (event.target);
    const bundleRow = targetElement?.closest('[data-gallery-wall-id]');
    const galleryWallId = bundleRow?.dataset?.galleryWallId;

    if (galleryWallId) {
      // Gallery wall bundle - update all items in the bundle. Routed through
      // the same mutation queue as regular lines so a bundle update and a
      // single-line update can't be in flight simultaneously.
      if (quantity === 0) {
        return this.#enqueueMutation(() => this.#removeGalleryWallBundle(galleryWallId));
      }
      return this.#enqueueMutation(() => this.#updateGalleryWallBundleQuantity(galleryWallId, quantity));
    }

    // Regular item handling. Prefer the stable line-item key; the 1-based
    // line index is only a fallback for older markup that lacks the key.
    const hasValidLine = typeof line === 'number' && line >= 1 && !isNaN(line);
    if (!key && !hasValidLine) {
      return;
    }

    // Resolve the row by key when available, otherwise by index.
    const lineItemRow = key
      ? this.refs.cartItemRows.find((row) => row.dataset.key === key)
      : this.refs.cartItemRows[line - 1];

    if (quantity === 0) {
      return this.onLineItemRemove({ line, key });
    }

    document.dispatchEvent(new CustomEvent('cart:quantity-changed', {
      detail: { line, key, quantity },
    }));
    this.updateQuantity({
      line,
      key,
      quantity,
      action: 'change',
    });

    if (!lineItemRow) return;

    const textComponent = /** @type {TextComponent | undefined} */ (lineItemRow.querySelector('text-component'));
    textComponent?.shimmer();
  }

  /**
   * Handles the line item removal.
   * @param {{ line?: number, key?: string }} target - The line item, addressed by key or 1-based index.
   */
  onLineItemRemove(target) {
    const { line, key } = typeof target === 'number' ? { line: target, key: undefined } : target;

    this.updateQuantity({
      line,
      key,
      quantity: 0,
      action: 'clear',
    });

    const cartItemRowToRemove = key
      ? this.refs.cartItemRows.find((row) => row.dataset.key === key)
      : this.refs.cartItemRows[line - 1];

    if (!cartItemRowToRemove) return;

    const rowsToRemove = [
      cartItemRowToRemove,
      // Get all nested lines of the row to remove
      ...this.refs.cartItemRows.filter((row) => row.dataset.parentKey === cartItemRowToRemove.dataset.key),
    ];

    // Add class to the row to trigger the animation
    rowsToRemove.forEach((row) => {
      const remove = () => row.remove();

      if (prefersReducedMotion()) return remove();

      row.style.setProperty('--row-height', `${row.clientHeight}px`);
      row.classList.add('removing');

      // Remove the row after the animation ends
      onAnimationEnd(row, remove);
    });
  }

  /**
   * Updates the quantity. The actual request is serialized through the
   * mutation queue so concurrent changes can never race.
   * @param {Object} config - The config.
   * @param {number} [config.line] - The 1-based line index (fallback addressing).
   * @param {string} [config.key] - The stable line-item key (preferred addressing).
   * @param {number} config.quantity - The quantity.
   * @param {string} config.action - The action.
   * @returns {Promise<unknown>}
   */
  updateQuantity(config) {
    return this.#enqueueMutation(() => this.#performQuantityUpdate(config));
  }

  /**
   * Performs a single, serialized quantity update against the cart.
   * @param {Object} config
   * @param {number} [config.line]
   * @param {string} [config.key]
   * @param {number} config.quantity
   * @param {string} config.action
   * @returns {Promise<void>}
   */
  async #performQuantityUpdate(config) {
    const cartPerformaceUpdateMarker = cartPerformance.createStartingMarker(`${config.action}:user-action`);

    const { line, key, quantity } = config;
    const { cartTotal } = this.refs;

    const cartItemsComponents = document.querySelectorAll('cart-items-component');
    const sectionsToUpdate = new Set([this.sectionId]);
    cartItemsComponents.forEach((item) => {
      if (item instanceof HTMLElement && item.dataset.sectionId) {
        sectionsToUpdate.add(item.dataset.sectionId);
      }
    });

    // Address the line item by stable key when available — `line` is a
    // positional index that shifts whenever another line is removed, so
    // a queued request built against a stale index would hit the wrong item.
    const body = JSON.stringify({
      ...(key ? { id: key } : { line }),
      quantity: quantity,
      sections: Array.from(sectionsToUpdate).join(','),
      sections_url: window.location.pathname,
    });

    cartTotal?.shimmer();

    try {
      const response = await fetch(`${Theme.routes.cart_change_url}`, fetchConfig('json', { body }));
      const responseText = await response.text();

      /** @type {any} */
      let parsedResponseText;
      try {
        parsedResponseText = JSON.parse(responseText);
      } catch {
        // Non-JSON body (throttle page, error HTML). Treat as a failed
        // mutation — the queue's settle handler clears the shimmer.
        throw new Error(`Cart change returned a non-JSON response (status ${response.status})`);
      }

      if (parsedResponseText.errors) {
        this.#handleCartError({ line, key }, parsedResponseText);
        return;
      }

      const newSectionHTML = new DOMParser().parseFromString(
        parsedResponseText.sections[this.sectionId],
        'text/html'
      );

      // Grab the new cart item count from a hidden element
      const newCartHiddenItemCount = newSectionHTML.querySelector('[ref="cartItemCount"]')?.textContent;
      const newCartItemCount = newCartHiddenItemCount ? parseInt(newCartHiddenItemCount, 10) : 0;

      this.dispatchEvent(
        new CartUpdateEvent({}, this.sectionId, {
          itemCount: newCartItemCount,
          source: 'cart-items-component',
          sections: parsedResponseText.sections,
        })
      );

      morphSection(this.sectionId, parsedResponseText.sections[this.sectionId]);
    } catch (error) {
      console.error(error);
    } finally {
      cartPerformance.measureFromMarker(cartPerformaceUpdateMarker);
    }
  }

  /**
   * Handles the discount update.
   * @param {DiscountUpdateEvent} event - The event.
   */
  handleDiscountUpdate = (event) => {
    this.#handleCartUpdate(event);
  };

  /**
   * Handles the cart error. Resolves the affected row by stable key when
   * available so a shifted line index can't surface the error on the wrong
   * item — and degrades gracefully (no throw) when the row is gone, since
   * throwing here produced an unhandled rejection that surfaced as a stray
   * error in the UI.
   * @param {{ line?: number, key?: string }} target - The affected line item.
   * @param {Object} parsedResponseText - The parsed response text.
   * @param {string} parsedResponseText.errors - The errors.
   */
  #handleCartError = (target, parsedResponseText) => {
    const { line, key } = target;

    // Resolve the row index — quantitySelectors / cartItemError refs are
    // index-keyed, so map the stable key back to a current position.
    let index = typeof line === 'number' ? line : undefined;
    if (key) {
      const rowIdx = this.refs.cartItemRows.findIndex((row) => row.dataset.key === key);
      if (rowIdx !== -1) index = rowIdx + 1;
    }

    if (index == null) {
      console.error('[CartItems] Cart error (row no longer present):', parsedResponseText.errors);
      return;
    }

    const quantitySelector = this.refs.quantitySelectors[index - 1];
    const quantityInput = quantitySelector?.querySelector('input');

    if (quantityInput) {
      quantityInput.value = quantityInput.defaultValue;
    }

    const cartItemError = this.refs[`cartItemError-${index}`];
    const cartItemErrorContainer = this.refs[`cartItemErrorContainer-${index}`];

    if (cartItemError instanceof HTMLElement && cartItemErrorContainer instanceof HTMLElement) {
      cartItemError.textContent = parsedResponseText.errors;
      cartItemErrorContainer.classList.remove('hidden');
    } else {
      console.error('[CartItems] Cart error:', parsedResponseText.errors);
    }
  };

  /**
   * Handles the cart update.
   *
   * @param {DiscountUpdateEvent | CartUpdateEvent | CartAddEvent} event
   */
  #handleCartUpdate = (event) => {
    if (event instanceof DiscountUpdateEvent) {
      sectionRenderer.renderSection(this.sectionId, { cache: false });
      return;
    }
    if (event.target === this) return;

    const cartItemsHtml = event.detail.data.sections?.[this.sectionId];
    if (cartItemsHtml) {
      morphSection(this.sectionId, cartItemsHtml);
    } else {
      sectionRenderer.renderSection(this.sectionId, { cache: false });
    }
  };

  /**
   * Disables the cart items.
   */
  #disableCartItems() {
    this.classList.add('cart-items-disabled');
  }

  /**
   * Enables the cart items.
   */
  #enableCartItems() {
    this.classList.remove('cart-items-disabled');
  }

  /**
   * Gets the section id.
   * @returns {string} The section id.
   */
  get sectionId() {
    const { sectionId } = this.dataset;

    if (!sectionId) throw new Error('Section id missing');

    return sectionId;
  }

  // ==========================================
  // Gallery Wall Bundle Methods
  // ==========================================

  /**
   * Sets up expand/collapse toggles for gallery wall bundles.
   * Uses event delegation on the component element for robustness after DOM morphs.
   */
  #setupGalleryWallToggles() {
    // Use event delegation - attach listener to this element once
    // This survives DOM morphs without needing to re-attach
    if (!this.#galleryWallToggleListenerAttached) {
      this.addEventListener('click', this.#handleGalleryWallToggle);
      this.#galleryWallToggleListenerAttached = true;
    }
  }

  /** @type {boolean} */
  #galleryWallToggleListenerAttached = false;

  /**
   * Handles gallery wall bundle expand/collapse toggle (event delegation)
   * @param {Event} event
   */
  #handleGalleryWallToggle = (event) => {
    const target = /** @type {HTMLElement} */ (event.target);

    // Bundle remove button (readymade combos)
    const removeBtn = target.closest('[data-remove-bundle]');
    if (removeBtn) {
      const bundleRow = removeBtn.closest('[data-gallery-wall-id]');
      const galleryWallId = bundleRow?.dataset?.galleryWallId;
      if (galleryWallId) this.#removeGalleryWallBundle(galleryWallId);
      return;
    }

    const button = target.closest('[data-toggle-gallery-wall-items]');

    if (!button) return;

    const targetId = button.getAttribute('aria-controls');
    const itemsContainer = targetId ? document.getElementById(targetId) : null;

    if (!itemsContainer) return;

    const isExpanded = button.getAttribute('aria-expanded') === 'true';
    const newExpanded = !isExpanded;

    button.setAttribute('aria-expanded', String(newExpanded));

    // Update toggle text
    const textSpan = button.querySelector('.cart-gallery-wall-bundle__toggle-text');
    if (textSpan) {
      const viewText = button.dataset.viewText;
      const hideText = button.dataset.hideText;
      textSpan.textContent = newExpanded ? hideText : viewText;
    }

    if (newExpanded) {
      itemsContainer.removeAttribute('hidden');
      button.classList.add('is-expanded');
    } else {
      itemsContainer.setAttribute('hidden', '');
      button.classList.remove('is-expanded');
    }
  };

  /**
   * Updates quantity for all items in a gallery wall bundle
   * @param {string} galleryWallId - The gallery wall ID
   * @param {number} quantity - The new quantity
   */
  async #updateGalleryWallBundleQuantity(galleryWallId, quantity) {
    const cartPerformaceUpdateMarker = cartPerformance.createStartingMarker('gallery-wall-bundle:quantity');

    // Find all items with this gallery wall ID
    const bundleItemKeys = this.#getGalleryWallBundleKeys(galleryWallId);

    if (bundleItemKeys.length === 0) return;

    // Build updates object for cart/update.js
    const updates = {};
    bundleItemKeys.forEach((key) => {
      updates[key] = quantity;
    });

    // Shimmer the price component
    const bundleRow = this.querySelector(`[data-gallery-wall-id="${galleryWallId}"]`);
    const textComponent = bundleRow?.querySelector('text-component');
    textComponent?.shimmer();

    try {
      const cartItemsComponents = document.querySelectorAll('cart-items-component');
      const sectionsToUpdate = new Set([this.sectionId]);
      cartItemsComponents.forEach((item) => {
        if (item instanceof HTMLElement && item.dataset.sectionId) {
          sectionsToUpdate.add(item.dataset.sectionId);
        }
      });

      const response = await fetch(`${Theme.routes.cart_update_url}`, fetchConfig('json', {
        body: JSON.stringify({
          updates,
          sections: Array.from(sectionsToUpdate).join(','),
          sections_url: window.location.pathname,
        }),
      }));

      const parsedResponse = await response.json();

      if (parsedResponse.errors) {
        console.error('[CartItems] Gallery wall bundle update error:', parsedResponse.errors);
        return;
      }

      const newSectionHTML = new DOMParser().parseFromString(
        parsedResponse.sections[this.sectionId],
        'text/html'
      );

      const newCartHiddenItemCount = newSectionHTML.querySelector('[ref="cartItemCount"]')?.textContent;
      const newCartItemCount = newCartHiddenItemCount ? parseInt(newCartHiddenItemCount, 10) : 0;

      this.dispatchEvent(
        new CartUpdateEvent({}, this.sectionId, {
          itemCount: newCartItemCount,
          source: 'cart-items-component',
          sections: parsedResponse.sections,
        })
      );

      morphSection(this.sectionId, parsedResponse.sections[this.sectionId]);
    } catch (error) {
      console.error('[CartItems] Gallery wall bundle update error:', error);
    } finally {
      cartPerformance.measureFromMarker(cartPerformaceUpdateMarker);
    }
  }

  /**
   * Removes all items in a gallery wall bundle
   * @param {string} galleryWallId - The gallery wall ID
   */
  async #removeGalleryWallBundle(galleryWallId) {
    const bundleItemKeys = this.#getGalleryWallBundleKeys(galleryWallId);

    if (bundleItemKeys.length === 0) {
      return;
    }

    // Set all items to quantity 0
    const updates = {};
    bundleItemKeys.forEach((key) => {
      updates[key] = 0;
    });

    // Add removal animation to bundle row
    const bundleRow = this.querySelector(`[data-gallery-wall-id="${galleryWallId}"]`);
    if (bundleRow) {
      bundleRow.style.setProperty('--row-height', `${bundleRow.clientHeight}px`);
      bundleRow.classList.add('removing');
    }

    await this.#updateGalleryWallBundleQuantity(galleryWallId, 0);
  }

  /**
   * Gets all cart item keys for a gallery wall bundle
   * @param {string} galleryWallId - The gallery wall ID
   * @returns {string[]} Array of cart item keys
   */
  #getGalleryWallBundleKeys(galleryWallId) {
    const keys = [];

    // The bundle row contains the main bundle info
    const bundleRow = this.querySelector(`[data-gallery-wall-id="${galleryWallId}"]`);
    if (!bundleRow) return keys;

    // Preferred path: the bundle row carries an explicit, server-rendered
    // list of every child line item key. This is authoritative — it does
    // not depend on the expandable items row being present or matchable,
    // so the remove action can never leave a child item orphaned.
    const explicitKeys = bundleRow.dataset.bundleKeys;
    if (explicitKeys) {
      explicitKeys
        .split(',')
        .map((k) => k.trim())
        .filter(Boolean)
        .forEach((k) => keys.push(k));
      if (keys.length > 0) return keys;
    }

    // Fallback (older markup without data-bundle-keys):
    // The items are in a separate row. Find the toggle row (next sibling) which has
    // aria-controls pointing to the items container
    const toggleRow = bundleRow.nextElementSibling;
    const toggleButton = toggleRow?.querySelector('[data-toggle-gallery-wall-items]');
    const itemsContainerId = toggleButton?.getAttribute('aria-controls');
    const itemsContainer = itemsContainerId ? document.getElementById(itemsContainerId) : null;

    // Get keys from expanded items in the separate items row
    if (itemsContainer) {
      const itemElements = itemsContainer.querySelectorAll('.cart-gallery-wall-item[data-key]');
      itemElements.forEach((el) => {
        const key = el.dataset.key;
        if (key) keys.push(key);
      });
    }

    // If no expanded items found, try to get the bundle row's own key
    // (this would be the first item's key as fallback)
    if (keys.length === 0) {
      const mainKey = bundleRow.dataset.key;
      if (mainKey) keys.push(mainKey);
    }

    return keys;
  }
}

if (!customElements.get('cart-items-component')) {
  customElements.define('cart-items-component', CartItemsComponent);
}
